import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { ThemeToggle } from '../components/ThemeToggle';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, loginWithGoogle, getPostLoginRoute } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(email, password);
    if (result.success) {
      if (result.pending_verification) {
        navigate('/signup/verify');
      } else {
        const products = result.user?.products || ['agent9'];
        if (products.length > 1) {
          navigate('/select-product');
        } else if (products[0] === 'voicedesk') {
          navigate('/voicedesk/dashboard');
        } else if (products[0] === 'tele_sale') {
          navigate('/telesales');
        } else {
          navigate('/dashboard');
        }
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="login-page">
      <div className="fixed top-4 right-4 z-50"><ThemeToggle compact /></div>
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0">
              <img src="/assets/agent9-icon.png" alt="Agent9" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-foreground tracking-tight">Agent9</span>
          </Link>
          <h1 className="text-2xl font-bold text-foreground mt-4" data-testid="login-heading">Log in to your account</h1>
          <p className="text-muted-foreground text-sm mt-1">Access Agent9 or VoiceDesk with one account</p>
        </div>

        <Card className="bg-card border-border backdrop-blur-sm">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="login-error">
                  {error}
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-foreground text-sm">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:text-primary/80" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-11 pr-10"
                    data-testid="login-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                data-testid="login-submit-btn">
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" />
                ) : (
                  <>Log in <ArrowRight size={16} className="ml-1" /></>
                )}
              </Button>
            </form>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-muted-foreground">or</span></div>
            </div>

            <button onClick={loginWithGoogle}
              className="w-full h-11 flex items-center justify-center gap-2 bg-muted hover:bg-accent border border-border rounded-lg text-sm text-foreground transition-colors"
              data-testid="google-login-btn">
              <svg width="16" height="16" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>
          </CardContent>
        </Card>

        <p className="text-center text-muted-foreground text-sm mt-6">
          Don't have an account? <Link to="/signup" className="text-primary hover:text-primary/80 font-medium" data-testid="signup-link">Create account</Link>
        </p>
      </div>
    </div>
  );
}
